import React, { useState, useEffect } from 'react';
import { getTermName } from './termUtils';
import StatusButton from './StatusButton';
import api from '../../../api';
import { 
  X, Eye, FileText, Calendar, User, Phone, MapPin, 
  GraduationCap, Hash, BadgeCheck, 
   Mail, School
} from 'lucide-react';

const StudentDetails = ({
  student,
  academicYears,
  onClose,
  onStatusChange,
  programs = [],
  programTypes = [],
}) => {
  const [profileLoading, setProfileLoading] = useState(true);
  const [fullProfile, setFullProfile] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [terms, setTerms] = useState([]);
  // Fetch terms if not semester-based
  useEffect(() => {
    // Assume academicYears has isSemesterBased flag or similar logic
    const ayId = student && (student.academicYearId?.$oid || student.academicYearId);
    const year = academicYears.find(y => (y._id?.$oid || y._id) === ayId);
    if (year && year.isSemesterBased === false && ayId) {
      // Fetch terms for this academic year
      api.get(`/api/academic-year/${ayId}/terms?tenantId=${student.tenantId}`)
        .then(res => setTerms(res.data.terms || []))
        .catch(() => setTerms([]));
    } else {
      setTerms([]);
    }
  }, [student, academicYears]);


  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchFullProfile = async () => {
      if (!student?._id && !student?.studentId) return;
      const id = student.studentId || student._id;
      try {
        setProfileLoading(true);
        const response = await api.get(`/api/students/profile/${id}`);
        setFullProfile(response.data.profile || response.data);
      } catch (error) {
        console.error("Failed to fetch profile", error);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchFullProfile();
  }, [student]);

  // --- LOGIC HELPERS ---
  
  // Robust ID resolver for MongoDB $oid vs string
  const resolveId = (idObj) => {
    if (!idObj) return null;
    if (typeof idObj === 'string') return idObj;
    return idObj.$oid || idObj.id || idObj._id || null;
  };

  const getProgramName = () => {
    const id = resolveId(student.programId);
    if (!id) return '-';
    // Match against programs list passed from parent
    const p = programs.find(x => resolveId(x._id) === id || resolveId(x.id) === id);
    return p ? p.name : '-';
  };

  const getProgramType = () => {
    const id = resolveId(student.programTypeId);
    if (!id) return '-';
    const t = programTypes.find(x => resolveId(x._id) === id || resolveId(x.id) === id);
    return t ? t.name : '-';
  };

  const getAcademicYearName = () => {
    const id = resolveId(student.academicYearId);
    const y = academicYears.find(x => resolveId(x._id) === id || resolveId(x.id) === id);
    return y ? y.name : '-';
  };

  const getSemesterName = () => {
    const ayId = resolveId(student.academicYearId);
    const semId = resolveId(student.semesterId);
    if (!ayId || !semId) return '-';
    const year = academicYears.find(y => resolveId(y._id) === ayId);
    const semester = year?.semesters?.find(s => resolveId(s._id) === semId);
    return semester ? semester.semesterName : '-';
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return '-';
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const calculateAge = (dateInput) => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age;
  };

  // Safe formatter to prevent [object Object] crashes
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val !== 'object') return val;
    
    // Handle Emergency Contact Object specifically
    if (val.relationship || val.number || val.countryCode) {
      return [val.relationship, val.countryCode, val.number].filter(Boolean).join(' ');
    }
    // Handle MongoDB Dates
    if (val.$date) return formatDate(val.$date);
    
    return '-'; 
  };

  // Removed handleUpdatePhoto: profile photo change is disabled

  const handlePreviewFile = (fileUrl, fileType) => {
    if (!fileUrl) return;
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
    let type = 'other';
    if (fileType === 'image' || fileUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)) type = 'image';
    else if (fileType === 'pdf' || fileUrl.match(/\.pdf$/i)) type = 'pdf';
    setPreviewDoc({ url: fullUrl, type });
  };

  // --- UI SUB-COMPONENTS ---

  const SectionTitle = ({ title, icon: Icon }) => (
    <div className="flex items-center gap-2 mb-6 pb-2 border-b border-slate-200">
      <div className="p-1.5 bg-slate-100 rounded-md text-slate-600">
        <Icon className="w-4 h-4" />
      </div>
      <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">{title}</h3>
    </div>
  );

  const InfoGroup = ({ label, value, fullWidth = false }) => (
    <div className={`${fullWidth ? 'col-span-2' : ''} flex flex-col gap-1`}>
      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
      <span className="text-sm font-medium text-slate-900 break-words leading-relaxed">{formatValue(value)}</span>
    </div>
  );

  const StatBox = ({ label, value, icon: Icon, color = "blue" }) => {
    const colorClasses = {
      blue: "bg-blue-50 text-blue-700 border-blue-100",
      emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
      violet: "bg-violet-50 text-violet-700 border-violet-100",
      amber: "bg-amber-50 text-amber-700 border-amber-100"
    };
    return (
      <div className={`flex items-center gap-4 p-4 rounded-xl border ${colorClasses[color]}`}>
        <div className={`p-3 rounded-lg bg-white shadow-sm`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="overflow-hidden">
          <p className="text-[10px] font-bold opacity-70 uppercase tracking-wider truncate">{label}</p>
          <p className="text-lg font-bold tracking-tight truncate">{value || '-'}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      
      {/* --- DOCUMENT PREVIEWER --- */}
      {previewDoc && (
        <div className="fixed inset-0 z-[70] bg-black/95 flex flex-col p-4 animate-in zoom-in-95 duration-200">
          <div className="flex justify-between items-center text-white mb-4 w-full max-w-6xl mx-auto">
            <h3 className="font-medium flex items-center gap-2 text-slate-300"><Eye className="w-4 h-4"/> Preview Mode</h3>
            <button onClick={() => setPreviewDoc(null)} className="p-2 hover:bg-white/10 rounded-full transition-colors"><X className="w-6 h-6"/></button>
          </div>
          <div className="flex-1 rounded-lg overflow-hidden flex items-center justify-center w-full h-full relative">
            {previewDoc.type === 'image' ? (
              <img src={previewDoc.url} alt="Doc" className="max-w-full max-h-full object-contain" />
            ) : (
              <iframe src={`${previewDoc.url}#toolbar=0`} className="w-full h-full border-none bg-white" title="Doc" />
            )}
          </div>
        </div>
      )}

      {/* --- MAIN CARD --- */}
      <div className="bg-slate-50 w-full max-w-6xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-200">
        
        {/* --- 1. HEADER --- */}
        <div className="bg-white px-8 py-6 border-b border-slate-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 shrink-0">
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="relative group">
              <div className="w-20 h-20 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shadow-inner">
                {student.profilePhotoUrl && student.profilePhotoUrl !== "[object Object]" ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/student-profile/${student.studentId || student._id}?t=${Date.now()}`} 
                    className="w-full h-full object-cover" 
                    alt="Profile"
                    onError={e => e.target.style.display='none'}
                  />
                ) : (
                  <span className="text-2xl font-bold text-slate-400">{student.firstName?.[0]}{student.lastName?.[0]}</span>
                )}
              </div>
              {/* Profile photo change disabled */}
            </div>

            {/* Title Block */}
            <div>
              <h1 className="text-2xl font-bold text-slate-900 leading-tight">{student.firstName} {student.lastName}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-slate-500 text-sm">
                <span className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-md border border-slate-200">
                  <Mail className="w-3.5 h-3.5" /> {student.email}
                </span>
                <span className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-bold uppercase tracking-wide ${
                  student.status === 'active' 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                    : 'bg-rose-50 text-rose-700 border-rose-200'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${student.status === 'active' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                  {student.status}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 self-end md:self-center">
            <StatusButton student={student} onStatusChange={onStatusChange} />
            <button onClick={onClose} className="p-2.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* --- 2. KEY METRICS BAR (USN & Admission No Separated) --- */}
        <div className="bg-white border-b border-slate-200 px-8 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
          <StatBox label="USN (University Serial No)" value={student.USN} icon={Hash} color="blue" />
          <StatBox label="Admission Number" value={student.admissionNumber} icon={BadgeCheck} color="emerald" />
          <StatBox label="Assigned Class" value={student.className} icon={School} color="violet" />
          <StatBox label="Academic Batch" value={getAcademicYearName()} icon={Calendar} color="amber" />
        </div>

        {/* --- 3. SCROLLABLE CONTENT --- */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* LEFT COLUMN (Personal & Contact) - 4 Cols */}
            <div className="lg:col-span-4 space-y-8">
              
              {/* Personal */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <SectionTitle title="Personal Info" icon={User} />
                <div className="grid grid-cols-2 gap-y-6 gap-x-4">
                  <InfoGroup label="Gender" value={student.gender} />
                  <InfoGroup label="Date of Birth" value={formatDate(student.dateOfBirth)} />
                  <InfoGroup label="Blood Group" value={student.bloodGroup} />
                  <InfoGroup label="Nationality" value={student.nationality} />
                  
                  <div className="col-span-2">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Age</span>
                    <span className="text-sm font-medium text-slate-900 block mt-1">
                      {calculateAge(student.dateOfBirth) ? `${calculateAge(student.dateOfBirth)} Years` : '-'}
                    </span>
                  </div>
                  
                  <InfoGroup label="Category" value={student.category} fullWidth />
                </div>
              </div>

              {/* Contact */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <SectionTitle title="Contact Details" icon={Phone} />
                <div className="space-y-6">
                  <InfoGroup label="Student Phone" value={student.studentPhoneNumber} />
                  <div className="h-px bg-slate-100 w-full" />
                  <InfoGroup label="Guardian Name" value={student.parentName} />
                  <InfoGroup label="Guardian Phone" value={student.parentPhoneNumber} />
                  <InfoGroup label="Guardian Email" value={student.parentEmail} />
                  
                  {/* Emergency Contact handled safely */}
                  {student.emergencyContact && (
                    <>
                      <div className="h-px bg-slate-100 w-full" />
                      <div className="p-3 bg-rose-50 rounded-lg border border-rose-100">
                        <p className="text-[10px] font-bold text-rose-400 uppercase tracking-wider mb-1">Emergency Contact</p>
                        <p className="text-sm font-medium text-rose-900 leading-relaxed">
                          {formatValue(student.emergencyContact)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN (Academic, Address, Education) - 8 Cols */}
            <div className="lg:col-span-8 space-y-8">
              
              {/* Academic Details */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <SectionTitle title="Academic Configuration" icon={GraduationCap} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
                  <InfoGroup label="Program Name" value={getProgramName()} />
                  <InfoGroup label="Program Type" value={getProgramType()} />
                  {(() => {
                    const ayId = student && (student.academicYearId?.$oid || student.academicYearId);
                    const year = academicYears.find(y => (y._id?.$oid || y._id) === ayId);
                    if (year && year.isSemesterBased === false) {
                      return <InfoGroup label="Current Term" value={getTermName(student, academicYears, terms, fullProfile)} />;
                    } else {
                      return <InfoGroup label="Current Semester" value={getSemesterName()} />;
                    }
                  })()}
                  <InfoGroup label="Admission Date" value={formatDate(student.admissionDate || student.createdAt)} />
                  <InfoGroup label="Transport Required" value={student.transportRequired ? 'Yes' : 'No'} />
                  <InfoGroup label="Hostel Required" value={student.hostelRequired ? 'Yes' : 'No'} />
                </div>
              </div>

              {/* Addresses */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                <SectionTitle title="Address Information" icon={MapPin} />
                {profileLoading ? (
                  <div className="h-24 animate-pulse bg-slate-100 rounded-lg"></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Permanent Address</p>
                      {fullProfile?.permanentAddress?.houseNoStreet ? (
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {fullProfile.permanentAddress.houseNoStreet}, {fullProfile.permanentAddress.cityTownVillage}<br/>
                          {fullProfile.permanentAddress.stateProvince}, {fullProfile.permanentAddress.country}<br/>
                          <span className="font-mono text-slate-500 mt-1 block">{fullProfile.permanentAddress.postalZipCode}</span>
                        </p>
                      ) : <span className="text-sm italic text-slate-400">Not provided</span>}
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Correspondence Address</p>
                      {fullProfile?.correspondenceAddress?.houseNoStreet ? (
                        <p className="text-sm text-slate-700 leading-relaxed">
                          {fullProfile.correspondenceAddress.houseNoStreet}, {fullProfile.correspondenceAddress.cityTownVillage}<br/>
                          {fullProfile.correspondenceAddress.stateProvince}, {fullProfile.correspondenceAddress.country}<br/>
                          <span className="font-mono text-slate-500 mt-1 block">{fullProfile.correspondenceAddress.postalZipCode}</span>
                        </p>
                      ) : <span className="text-sm italic text-slate-400">Same as Permanent</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Education History Table */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-6 border-b border-slate-100">
                  <SectionTitle title="Previous Education" icon={FileText} />
                </div>
                <div className="overflow-x-auto">
                  {fullProfile?.previousEducation?.length > 0 ? (
                    <table className="w-full text-sm text-left">
                      <thead className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-4">Institution</th>
                          <th className="px-6 py-4">Class</th>
                          <th className="px-6 py-4">Year</th>
                          <th className="px-6 py-4">Score</th>
                          <th className="px-6 py-4 text-right">Proof</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {fullProfile.previousEducation.map((edu, i) => (
                          <tr key={i} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-4 font-medium text-slate-900">{edu.institutionName}</td>
                            <td className="px-6 py-4 text-slate-600">{edu.previousClassGrade}</td>
                            <td className="px-6 py-4 text-slate-600 font-mono">{edu.yearOfPassing}</td>
                            <td className="px-6 py-4"><span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">{edu.result}</span></td>
                            <td className="px-6 py-4 text-right">
                              {edu.educationProof ? (
                                <button 
                                  onClick={() => handlePreviewFile(edu.educationProof.fileUrl, edu.educationProof.fileType)}
                                  className="text-blue-600 hover:text-blue-800 font-semibold text-xs inline-flex items-center gap-1 transition-colors px-3 py-1.5 bg-blue-50 rounded-lg hover:bg-blue-100"
                                >
                                  <Eye className="w-3.5 h-3.5" /> View
                                </button>
                              ) : <span className="text-slate-300 text-xs italic">Empty</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-10 text-center text-slate-400 italic bg-slate-50/30">
                      No previous education history found.
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDetails;